import { useState, useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import TeacherShell from '../../../components/layout/TeacherShell';
import { Card, Button, Input, Textarea } from '../../../components/ui';
import { cn } from '../../../lib/cn';

const TYPE_BADGE_STYLES = {
  epub: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  pdf: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  pptx: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  link: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
};

function ItemTypeBadge({ itemType }) {
  const label = (itemType || 'epub').toUpperCase();
  const style = TYPE_BADGE_STYLES[itemType] || TYPE_BADGE_STYLES.epub;
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', style)}>
      {label}
    </span>
  );
}

export default function BookSeriesShow({ series, books = [], available_books = [] }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(series.name || '');
  const [author, setAuthor] = useState(series.author || '');
  const [description, setDescription] = useState(series.description || '');
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [pickerBookId, setPickerBookId] = useState('');
  const [pickerPosition, setPickerPosition] = useState('');

  const unassignedBooks = useMemo(
    () => available_books.filter((b) => !b.in_this_series),
    [available_books]
  );

  const scrollTitle = (
    <>
      <Link href="/teacher/library" className="hover:text-blue-600 dark:hover:text-blue-400">
        Library
      </Link>
      {'  ›  Series ›  '}
      {series.name}
    </>
  );

  const cancelEdit = () => {
    setName(series.name || '');
    setAuthor(series.author || '');
    setDescription(series.description || '');
    setEditing(false);
  };

  const saveEdit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    router.patch(
      `/teacher/book_series/${series.id}`,
      {
        book_series: {
          name: name.trim(),
          author: author.trim() || null,
          description: description.trim() || null,
        },
      },
      {
        onSuccess: () => setEditing(false),
        onFinish: () => setSaving(false),
      }
    );
  };

  const handleDelete = () => {
    if (!confirm(`Delete "${series.name}"? Books in the series will not be deleted.`)) return;
    router.delete(`/teacher/book_series/${series.id}`);
  };

  const handleAddBook = (e) => {
    e.preventDefault();
    if (!pickerBookId) return;
    setAdding(true);
    router.post(
      `/teacher/book_series/${series.id}/add_book`,
      {
        library_item_id: pickerBookId,
        position_in_series: pickerPosition || null,
      },
      {
        onSuccess: () => {
          setPickerBookId('');
          setPickerPosition('');
        },
        onFinish: () => setAdding(false),
      }
    );
  };

  const handleRemoveBook = (book) => {
    if (!confirm(`Remove "${book.title}" from this series?`)) return;
    router.delete(`/teacher/book_series/${series.id}/remove_book`, {
      data: { library_item_id: book.id },
    });
  };

  const handleUpdatePosition = (book, nextPosition) => {
    const value = nextPosition === '' ? null : Number(nextPosition);
    if (value === book.position_in_series) return;
    router.post(
      `/teacher/book_series/${series.id}/add_book`,
      { library_item_id: book.id, position_in_series: value },
      { preserveScroll: true }
    );
  };

  const canEdit = series.is_own;

  return (
    <TeacherShell scrollTitle={scrollTitle}>
      <div className="mb-6">
        <Link
          href="/teacher/library"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-4 transition-colors"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Library
        </Link>

        {editing ? (
          <Card className="p-6">
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Author</label>
                <Input
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Optional — useful when every book shares an author"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Optional"
                />
              </div>
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={saving || !name.trim()}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={cancelEdit}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        ) : (
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{series.name}</h1>
              {series.author && (
                <p className="mt-2 text-gray-500 dark:text-gray-400">by {series.author}</p>
              )}
              {series.description && (
                <p className="mt-3 text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                  {series.description}
                </p>
              )}
              {!series.is_own && series.owner_name && (
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                  Curated by {series.owner_name}
                </p>
              )}
            </div>

            {canEdit && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-sm font-medium text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Card className="p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Books in this series ({books.length})
        </h2>

        {books.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No books yet. {canEdit && 'Add one below.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {books.map((book) => (
              <li key={book.id} className="py-3 flex items-center gap-4">
                {canEdit ? (
                  <PositionInput
                    value={book.position_in_series}
                    onCommit={(next) => handleUpdatePosition(book, next)}
                  />
                ) : (
                  <span className="w-12 text-sm text-gray-400 dark:text-gray-500 font-mono text-center">
                    {book.position_in_series != null ? `#${book.position_in_series}` : '—'}
                  </span>
                )}

                <div className="flex-1 min-w-0">
                  <Link
                    href={`/teacher/library/${book.id}`}
                    className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 line-clamp-1"
                  >
                    {book.title}
                  </Link>
                  {book.author && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{book.author}</p>
                  )}
                </div>

                <ItemTypeBadge itemType={book.item_type} />

                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleRemoveBook(book)}
                    title="Remove from series"
                    className="p-1.5 rounded text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {canEdit && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add a book</h2>
          {unassignedBooks.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No more unassigned books in your library.{' '}
              <Link href="/teacher/library" className="text-blue-600 dark:text-blue-400 hover:underline">
                Upload one
              </Link>
              .
            </p>
          ) : (
            <form onSubmit={handleAddBook} className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[16rem]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Book</label>
                <select
                  value={pickerBookId}
                  onChange={(e) => setPickerBookId(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/20"
                  required
                >
                  <option value="">Choose a book…</option>
                  {unassignedBooks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.title}
                      {b.author ? ` — ${b.author}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-28">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Position</label>
                <Input
                  type="number"
                  min="1"
                  value={pickerPosition}
                  onChange={(e) => setPickerPosition(e.target.value)}
                  placeholder="—"
                />
              </div>
              <Button type="submit" disabled={adding || !pickerBookId}>
                {adding ? 'Adding…' : 'Add'}
              </Button>
            </form>
          )}
        </Card>
      )}
    </TeacherShell>
  );
}

function PositionInput({ value, onCommit }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value));

  return (
    <input
      type="number"
      min="1"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      placeholder="—"
      title="Position in series"
      className="w-12 text-center px-1 py-1 text-sm rounded border border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-gray-300 dark:focus:border-gray-500 bg-transparent text-gray-700 dark:text-gray-300 font-mono focus:outline-none"
    />
  );
}
